import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatCard } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowLeft, Clock, ShieldCheck, Wallet, FileText, Gauge, Lightbulb, MessageCircle, ScrollText } from "lucide-react";

const RATING = { A: { v: "default", t: "Reliable payer" }, B: { v: "secondary", t: "Average" }, C: { v: "destructive", t: "Watch closely" } };

export default function CustomerDetail() {
  const [sharing, setSharing] = useState(false);
  const shareStatement = async () => {
    setSharing(true);
    try {
      const { data: s } = await api.post(`/admin/customers/${id}/statement/share`, { origin: window.location.origin });
      window.open(s.whatsapp_url, "_blank");
      toast.success(s.phone ? `Opening WhatsApp for ${s.phone} · balance ${money(s.open_balance)}` : "No phone on file - pick the contact in WhatsApp");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSharing(false);
    }
  };
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [days, setDays] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get(`/admin/customers/${id}`).then((r) => { setC(r.data); setDays(String(r.data.credit_days)); }), [id]);
  useEffect(() => { load(); }, [load]);

  const apply = async (v, reason) => {
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${id}/credit-days`, { credit_days: Number(v), reason });
      toast.success(`Credit period set to ${v} days - applies to new invoices`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!c) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const s = c.stats;
  const r = RATING[c.rating];

  return (
    <div data-testid="customer-detail">
      <Link to="/dashboard/customers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="customer-back-link"><ArrowLeft className="w-4 h-4" /> Customers</Link>
      <PageHeader
        title={c.name}
        subtitle={`${c.phone || "no phone"} · onboarded ${c.onboarding_date} by ${c.onboarding_agent || "—"}`}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={r.v} className="text-sm px-3 py-1" data-testid="customer-rating">Rating {c.rating} · {r.t}</Badge>
            <Button variant="outline" size="sm" onClick={() => window.open(`/customer-statement/${id}`, "_blank")} data-testid="customer-statement-button"><ScrollText className="w-4 h-4 mr-1" /> Statement</Button>
            <Button size="sm" onClick={shareStatement} disabled={sharing} data-testid="customer-whatsapp-statement-button">
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp statement</>}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Lifetime sales" value={money(s.lifetime_value)} icon={Wallet} sub={`${s.invoices} invoice(s) · ${s.returns} return(s)`} />
        <StatCard label="Unpaid now" value={money(s.unpaid_value)} icon={FileText} sub={`${s.unpaid_invoices} open · ${s.overdue_now} overdue`} accent={s.overdue_now > 0} />
        <StatCard label="Paid on time" value={`${s.on_time_pct}%`} icon={Gauge} sub={`${s.on_time} on time · ${s.late} late (max ${s.max_days_late}d)`} />
        <StatCard label="Avg days to pay" value={s.avg_days_to_pay} icon={Clock} sub={`credit period ${c.credit_days} days`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-5 lg:col-span-2" data-testid="credit-suggestion-card">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-display font-semibold">Suggested credit period: <span className="text-primary" data-testid="suggested-credit-days">{c.suggested_credit_days} days</span>{c.suggested_credit_days !== c.credit_days && <span className="text-xs text-muted-foreground font-normal"> (currently {c.credit_days})</span>}</h3>
              <p className="text-sm text-muted-foreground mt-1">{c.suggestion_reason}</p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                {c.suggested_credit_days !== c.credit_days && (
                  <Button size="sm" disabled={saving} onClick={() => apply(c.suggested_credit_days, "Applied system suggestion")} data-testid="apply-suggestion-button">Apply {c.suggested_credit_days} days</Button>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <Input type="number" min="0" max="180" className="h-9 w-24" value={days} onChange={(e) => setDays(e.target.value)} data-testid="credit-days-input" />
                  <Button size="sm" variant="outline" disabled={saving || Number(days) === c.credit_days} onClick={() => apply(days, "Manual")} data-testid="save-credit-days-button">Set manually</Button>
                </div>
              </div>
              {c.credit_history.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-3">History: {c.credit_history.map((h) => `${h.at.slice(0, 10)} ${h.old ?? "—"}→${h.new}`).join(" · ")}</p>
              )}
            </div>
          </div>
        </Card>
        <Card className="p-5" data-testid="ownership-card">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4 text-primary" /><h3 className="font-display font-semibold">Account ownership</h3></div>
          <p className="text-sm">{c.onboarding_agent || "—"}</p>
          <p className="text-xs text-muted-foreground mt-1">{c.ownership_active ? `Repeat orders credited to this agent until ${c.ownership_until}` : `Ownership window ended ${c.ownership_until || "—"} - open to any agent`}</p>
          <Badge variant={c.ownership_active ? "default" : "outline"} className="mt-3">{c.ownership_active ? "Active" : "Expired"}</Badge>
          <p className="text-xs text-muted-foreground mt-3">{c.is_new ? "Counted as NEW customer for bonus" : "Repeat customer"}{s.deposit_adjustments > 0 && ` · ${s.deposit_adjustments} deposit adjustment(s)`}</p>
        </Card>
      </div>

      <Card className="overflow-hidden mt-4">
        <div className="px-4 py-3 border-b border-border"><h3 className="font-display font-semibold">Order history & payment track record</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-medium px-4 py-2">Invoice</th>
                <th className="text-left font-medium px-4 py-2">Date</th>
                <th className="text-left font-medium px-4 py-2">Due</th>
                <th className="text-left font-medium px-4 py-2">Paid</th>
                <th className="text-left font-medium px-4 py-2">Agent</th>
                <th className="text-left font-medium px-4 py-2">Items</th>
                <th className="text-right font-medium px-4 py-2">Value</th>
                <th className="text-right font-medium px-4 py-2">Days to pay</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {c.orders.map((o) => (
                <tr key={o.order_id} className="border-b border-border/50" data-testid={`customer-order-${o.order_id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs"><Link to={`/invoice/${o.order_id}`} className="text-primary hover:underline">{o.invoice_no}</Link></td>
                  <td className="px-4 py-2.5 text-muted-foreground">{o.date}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{o.due_date}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{o.paid_date || "—"}</td>
                  <td className="px-4 py-2.5">{o.agent}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{o.items}</td>
                  <td className="px-4 py-2.5 text-right">{money(o.value)}</td>
                  <td className="px-4 py-2.5 text-right">{o.days_to_pay ?? "—"}{o.days_late > 0 && <span className="text-destructive text-xs"> (+{o.days_late}d late)</span>}</td>
                  <td className="px-4 py-2.5">
                    {o.is_return ? <Badge variant="destructive">Return</Badge> : <Badge variant={o.payment_status === "paid" ? "default" : o.days_late > 0 ? "destructive" : "outline"} className="capitalize">{o.payment_status === "paid" ? "paid" : o.days_late > 0 ? "overdue" : o.payment_status}</Badge>}
                    {o.deposit_adjustment && <Badge variant="secondary" className="ml-1 capitalize">{o.deposit_adjustment.replace("_", " ")}</Badge>}
                  </td>
                </tr>
              ))}
              {c.orders.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground text-sm">No invoices yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
